import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/config';
import {
  DataProvider,
  QBox,
  QDataTable,
  QHeading,
  QLink,
  QSelect,
  QSelectItem,
  QStack,
  QSwitch,
  QText,
} from '@qualio/ui-components';
import {
  EventTemplateField,
  EventTemplateFieldAttributeName,
  EventTemplateFieldAttributeNames,
  EventTemplateFormValues,
} from '../../../../types';
import * as DisplayStrings from '../../../../displayStrings';

type TemplateFieldsListProps = {
  templateFields: EventTemplateFormValues['fields'];
  setTemplateFields: (templateFields: EventTemplateFormValues['fields']) => void;
  isTemplateReadonly: boolean;
  templateSteps: EventTemplateFormValues['steps'];
};

type Option = {
  label: string;
  description: string;
  linkHref?: string;
  linkText?: string;
  attributeName: EventTemplateFieldAttributeName;
};

type OptionValue = Option & {
  isVisible: boolean;
  isMandatory: boolean;
  workflowStepId: number;
};

export const FieldOptions: Option[] = [
  {
    label: i18n.t('common.labels.product'),
    description: i18n.t('events.messages.selectProductForEvent'),
    attributeName: EventTemplateFieldAttributeNames.enum.product,
  },
  {
    label: DisplayStrings.RootCause,
    description: i18n.t('events.messages.selectRootCauseForEvent'),
    attributeName: EventTemplateFieldAttributeNames.enum.root_cause,
  },
  {
    label: DisplayStrings.Risk,
    description: i18n.t('events.messages.riskCalculationDescription'),
    linkText: 'Please see our documentation for more details.',
    linkHref: 'https://docs.qualio.com/en/articles/6644756-events-advanced-details-functionality#h_3b5d997a7d',
    attributeName: EventTemplateFieldAttributeNames.enum.risk,
  },
  {
    label: DisplayStrings.RelatedSuppliers,
    description: i18n.t('events.messages.linkSuppliersToEvent'),
    attributeName: EventTemplateFieldAttributeNames.enum.supplier,
  },
];

export const EventTemplateFormFieldsTable = ({
  templateFields,
  setTemplateFields,
  isTemplateReadonly,
  templateSteps,
}: TemplateFieldsListProps) => {
  const { t } = useTranslation();
  const formSteps = useMemo(() => {
    const steps = templateSteps
      .filter((step) => step.type === 'form')
      .sort((a, b) => a.order - b.order)
      .map((step) => {
        return { label: step.label, value: step.id };
      });
    return [
      { label: DisplayStrings.EventProperties, value: 0 },
      { label: t('common.labels.formSteps'), options: steps },
    ];
  }, [templateSteps, t]);

  const createOrUpdateField = useCallback(
    (attributeName: EventTemplateFieldAttributeName, mandatory: boolean) => {
      const originalField = templateFields[attributeName] ? templateFields[attributeName] : {};
      const newField: EventTemplateField = {
        ...originalField,
        attribute_name: attributeName,
        mandatory,
      };
      const templateFieldsCopy = { ...templateFields, [attributeName]: newField };
      setTemplateFields(templateFieldsCopy);
    },
    [templateFields, setTemplateFields],
  );

  const handleSetVisible = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, attributeName: EventTemplateFieldAttributeName) => {
      if (event.target.checked) {
        createOrUpdateField(attributeName, false);
      } else {
        const templateFieldsCopy = { ...templateFields };
        delete templateFieldsCopy[attributeName];
        setTemplateFields(templateFieldsCopy);
      }
    },
    [templateFields, createOrUpdateField, setTemplateFields],
  );

  const handleStepSelect = useCallback(
    (event: QSelectItem<string, number | null | undefined> | null, attributeName: EventTemplateFieldAttributeName) => {
      const originalField = templateFields[attributeName] ?? {};
      const workflow_step_id = event?.value === 0 ? null : event?.value;
      const newField = {
        ...originalField,
        workflow_step_id: workflow_step_id,
      } as EventTemplateField;
      const templateFieldsCopy = { ...templateFields, [attributeName]: newField };
      setTemplateFields(templateFieldsCopy);
    },
    [templateFields, setTemplateFields],
  );

  const data = useMemo(
    () =>
      FieldOptions.map((option) => {
        const isVisible = Boolean(templateFields[option.attributeName]);
        const isMandatory = isVisible ? templateFields[option.attributeName]!.mandatory : false;

        const field = templateFields[option.attributeName];
        const filedWorkflowStep = templateSteps.find((s) => s.id === field?.workflow_step_id);

        return {
          label: option.label,
          description: option.description,
          attributeName: option.attributeName,
          linkHref: option.linkHref,
          linkText: option.linkText,
          isVisible,
          isMandatory,
          workflowStepId: filedWorkflowStep?.id ?? 0,
        };
      }),
    [templateFields, templateSteps],
  );

  return (
    <QStack spacing="30px">
      <QStack>
        <QHeading size="md">{t('common.labels.fields')}</QHeading>
        <QText color="gray.500" fontSize="sm">
          {DisplayStrings.TemplateFieldControlDescriptionForEventCapture}
        </QText>
      </QStack>
      <DataProvider.Fixed data={data}>
        <QDataTable
          data-cy="event-template-form-fields-table"
          columns={[
            {
              header: t('common.labels.title'),
              accessorKey: 'label',
              enableSorting: false,
              meta: {
                minWidth: '150px',
                maxWidth: '150px',
              },
            },
            {
              accessorKey: 'title',
              header: t('common.labels.description'),
              enableSorting: false,
              cell: ({ row: { original: field } }: { row: { original: OptionValue } }) => {
                return (
                  <>
                    {field.description}
                    {field.linkHref && field.linkText ? (
                      <QLink isExternal href={field.linkHref} isCrossMFE={false}>
                        {field.linkText}
                      </QLink>
                    ) : null}
                  </>
                );
              },
            },

            {
              header: t('common.labels.visible'),
              id: 'visible',
              enableSorting: false,
              cell: ({ row: { original: field } }: { row: { original: OptionValue } }) => {
                return (
                  <QSwitch
                    data-cy={`EventTemplate-fieldVisible-${field.attributeName}`}
                    key={`EventTemplate-fieldVisible-${field.attributeName}`}
                    onChange={(e) => handleSetVisible(e, field.attributeName)}
                    isChecked={field.isVisible}
                    isDisabled={isTemplateReadonly}
                  />
                );
              },
              meta: {
                minWidth: '100px',
                maxWidth: '100px',
              },
            },

            {
              header: t('common.labels.required'),
              id: 'required',
              enableSorting: false,
              cell: ({ row: { original: field } }: { row: { original: OptionValue } }) => {
                return (
                  <QSwitch
                    data-cy={`EventTemplate-fieldMandatory-${field.attributeName}`}
                    isDisabled={isTemplateReadonly || !field.isVisible}
                    onChange={(e) => createOrUpdateField(field.attributeName, e.target.checked)}
                    isChecked={field.isMandatory}
                  />
                );
              },
              meta: {
                minWidth: '100px',
                maxWidth: '100px',
              },
            },

            {
              header: t('common.labels.capturedIn'),
              id: 'capturedIn',
              enableSorting: false,
              cell: ({ row: { original: field } }: { row: { original: OptionValue } }) => {
                return (
                  <QBox data-cy={`event-template-data-capture-${field.attributeName}`}>
                    <QSelect
                      isDisabled={isTemplateReadonly || !field.isVisible}
                      onChange={(e) => handleStepSelect(e, field.attributeName)}
                      options={formSteps}
                      value={field.workflowStepId}
                    />
                  </QBox>
                );
              },
              meta: {
                minWidth: '300px',
                maxWidth: '300px',
              },
            },
          ]}
          hideItemCount
        />
      </DataProvider.Fixed>
    </QStack>
  );
};
